/** The project has no chart dependency and these forms are few and small, so these are hand-rolled rather than pulling in a library for one page. */
import { Inbox } from 'lucide-react'
import { useId, useLayoutEffect, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import {
  donutArcs,
  dotDomain,
  fraction,
  initials,
  largestRemainder,
  seriesPoints,
  smoothAreaPath,
  smoothPath,
  stepActive,
  tipSide,
  waffleCells,
  waffleColumns,
} from './chartGeometry'

import { nearestPointIndex, readoutAlign, steppedPointIndex } from './sparklineScrub'
import { tileShade, treemapTiles } from './treemapLayout'
import { mutedTextClass } from './ui'
import { HelpTip } from './ui/HelpTip'

// Not a chart: one number's job is to be read, and a plot around it would add ink without adding
// information.
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  to,
  density = 'default',
  visual,
  visualBelow = false,
  action,
}: {
  label: string
  value: ReactNode
  hint?: string
  visual?: ReactNode
  /** A dense tile with no hint draws its visual full width beneath the value, for a value too wide to share a row with it. */
  visualBelow?: boolean
  /** A small control in the title row of a dense tile; it must not add height. */
  action?: ReactNode
  tone?: 'neutral' | 'good' | 'warning' | 'critical'
  to?: string
  density?: 'default' | 'dense'
}) {
  const toneDot =
    tone === 'good'
      ? 'bg-emerald-600 dark:bg-emerald-400'
      : tone === 'warning'
        ? 'bg-amber-600 dark:bg-amber-400'
        : tone === 'critical'
          ? 'bg-red-600 dark:bg-red-400'
          : undefined
  const className = `block rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 ${
    density === 'dense' ? 'flex h-28 flex-col overflow-hidden p-3' : 'p-4'
  }${to ? ' hover:border-slate-300 hover:shadow-sm dark:hover:border-slate-600' : ''}`
  const body =
    density === 'dense' ? (
      <>
        <div className="flex min-w-0 shrink-0 items-center gap-1.5">
          {toneDot && <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${toneDot}`} />}
          <span className="text-[11px] leading-tight text-text-muted">{label}</span>
          {action && <span className="ml-auto flex shrink-0 items-center">{action}</span>}
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-start gap-3">
          <span className="shrink-0 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {value}
          </span>
          {visual && !visualBelow && <span className="flex min-w-0 flex-1 items-center">{visual}</span>}
        </div>
        {visual && visualBelow && <div className="flex min-w-0 shrink-0 items-center">{visual}</div>}
        {hint && (
          <div className="line-clamp-2 min-w-0 shrink-0 text-[11px] leading-tight text-slate-500 dark:text-slate-400">
            {hint}
          </div>
        )}
      </>
    ) : (
      <>
        <div className="flex items-center gap-1.5">
          {toneDot && <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${toneDot}`} />}
          <span className={mutedTextClass}>{label}</span>
        </div>
        <div className="mt-1 flex items-center gap-3">
          <span className="shrink-0 text-2xl font-semibold text-slate-900 dark:text-slate-100">{value}</span>
          {visual && <span className="flex min-w-0 flex-1 items-center">{visual}</span>}
        </div>
        {hint && <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</div>}
      </>
    )
  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  )
}

function ChartFrame({
  title,
  subtitle,
  hint,
  children,
  legend,
  fill,
}: {
  title: string
  subtitle?: string
  hint?: string
  children: React.ReactNode
  legend?: React.ReactNode
  fill?: boolean
}) {
  return (
    <figure
      className={`viz-root m-0 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800 ${fill ? 'flex flex-col' : ''}`}
    >
      <figcaption className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {title}
            {hint && (
              <span className="ml-1">
                <HelpTip label={title}>{hint}</HelpTip>
              </span>
            )}
          </h3>
          {subtitle && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
          )}
        </div>
        {legend}
      </figcaption>
      {children}
    </figure>
  )
}

export interface Bucket {
  label: string
  value: number
}

const ORDINAL_RAMP = ['var(--viz-o1)', 'var(--viz-o2)', 'var(--viz-o3)', 'var(--viz-o4)']
/** Young to old: an ageing queue reads green to red, and every bar still prints its own count. */
const AGE_RAMP = ['var(--viz-good)', 'var(--viz-warning)', 'var(--viz-s2)', 'var(--viz-critical)']
const CATEGORICAL_RAMP = [
  'var(--viz-c1)',
  'var(--viz-c2)',
  'var(--viz-c3)',
  'var(--viz-c4)',
  'var(--viz-c5)',
  'var(--viz-c6)',
]

/** Show every `step`-th axis label so a long series (13 weeks in a third-width card) does not print labels on top of each other. */
export function axisLabelStep(count: number, maxLabels = 6): number {
  if (count <= maxLabels) return 1
  return Math.ceil(count / maxLabels)
}

/** Bars that grow from their baseline on mount; a transform, so the layout never reflows, and nothing moves under reduced motion. */
export const BAR_GROW_X =
  'origin-left motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-out motion-safe:starting:scale-x-0'
export const BAR_GROW_Y =
  'origin-bottom motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-out motion-safe:starting:scale-y-0'

/** Each bar starts a beat after the one before it, capped so a long series still lands inside half a second. */
export function barDelay(index: number): string {
  return `${Math.min(Math.max(index, 0), 8) * 30}ms`
}

/** Axis label classes: words stay whole, so a single word too wide for its bar ends in an ellipsis instead of splitting mid-word. */
export function axisLabelClass(label: string, index: number, step: number): string {
  if (step !== 1) return index % step === 0 ? 'whitespace-nowrap' : 'invisible'
  return /\s/.test(label.trim())
    ? 'min-h-8 max-w-full break-normal text-center leading-4 line-clamp-2'
    : 'min-h-8 max-w-full truncate text-center leading-4'
}

export function OrdinalBarChart({
  title,
  subtitle,
  hint,
  buckets,
  format,
  palette = 'ordinal',
  emptyLabel,
  animate = false,
  caption,
  compact = false,
}: {
  title: string
  subtitle?: string
  hint?: string
  buckets: Bucket[]
  format: (n: number) => string
  palette?: 'ordinal' | 'categorical' | 'single' | 'age'
  emptyLabel?: string
  animate?: boolean
  caption?: string
  /** A shorter plot for a card that shares a slide with small neighbours. */
  compact?: boolean
}) {
  const [hover, setHover] = useState<number | null>(null)
  const ramp =
    palette === 'categorical'
      ? CATEGORICAL_RAMP
      : palette === 'single'
        ? ['var(--viz-s1)']
        : palette === 'age'
          ? AGE_RAMP
          : ORDINAL_RAMP
  const max = Math.max(1, ...buckets.map((b) => b.value))
  const step = axisLabelStep(buckets.length)

  if (buckets.every((b) => b.value === 0)) {
    return (
      <ChartFrame title={title} subtitle={subtitle} hint={hint} fill>
        <EmptyPlot label={emptyLabel} />
      </ChartFrame>
    )
  }

  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint}>
      <div
        className={`flex items-end gap-2 ${compact ? 'h-[140px]' : 'h-[210px]'}`}
        role="img"
        aria-label={`${title}: ${buckets.map((b) => `${b.label} ${format(b.value)}`).join(', ')}`}
      >
        {buckets.map((b, i) => (
          <div
            key={b.label}
            className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <span
              className={`text-xs tabular-nums text-slate-700 dark:text-slate-200 ${
                hover === i ? 'font-semibold' : ''
              }`}
            >
              {format(b.value)}
            </span>
            <div
              className={animate ? `w-full rounded-t ${BAR_GROW_Y}` : 'w-full rounded-t'}
              style={{
                height: `${Math.max(2, (b.value / max) * (compact ? 80 : 140))}px`,
                background: ramp[Math.min(i, ramp.length - 1)],
                ...(animate ? { transitionDelay: barDelay(i) } : {}),
              }}
            />
            <span
              title={b.label}
              className={`text-xs text-slate-500 dark:text-slate-400 ${axisLabelClass(b.label, i, step)}`}
            >
              {b.label}
            </span>
          </div>
        ))}
      </div>
      {caption && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{caption}</p>}
    </ChartFrame>
  )
}

export function RankedBarChart({
  title,
  subtitle,
  hint,
  buckets,
  format,
  emptyLabel,
  onPick,
  picked,
  colors,
  animate = false,
}: {
  title: string
  subtitle?: string
  hint?: string
  buckets: Bucket[]
  format: (n: number) => string
  emptyLabel: string
  onPick?: (label: string) => void
  picked?: string
  colors?: string[]
  animate?: boolean
}) {
  const max = Math.max(1, ...buckets.map((b) => b.value))
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint} fill={buckets.length === 0}>
      {buckets.length === 0 ? (
        <EmptyPlot label={emptyLabel} />
      ) : (
        <ul
          className="m-0 flex list-none flex-col gap-2 p-0"
          aria-label={title}
        >
          {buckets.map((b, i) => {
            const row = (
              <>
                <span className="w-[38%] max-w-28 shrink-0 truncate text-slate-600 dark:text-slate-300" title={b.label}>
                  {b.label}
                </span>
                <span className="h-4 min-w-0 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-700/40">
                  <span
                    className={animate ? `block h-full rounded ${BAR_GROW_X}` : 'block h-full rounded'}
                    style={{
                      width: `${(b.value / max) * 100}%`,
                      background: colors ? colors[Math.min(i, colors.length - 1)] : 'var(--viz-s1)',
                      ...(animate ? { transitionDelay: barDelay(i) } : {}),
                    }}
                  />
                </span>
                <span className="w-[24%] max-w-24 shrink-0 text-right tabular-nums text-slate-700 dark:text-slate-200">
                  {format(b.value)}
                </span>
              </>
            )
            return (
              <li key={`${i}-${b.label}`} className="flex items-center gap-2 text-xs">
                {onPick ? (
                  <button
                    type="button"
                    onClick={() => onPick(b.label)}
                    aria-pressed={picked === b.label}
                    className={`flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40 ${
                      picked === b.label ? 'ring-1 ring-blue-500' : ''
                    }`}
                  >
                    {row}
                  </button>
                ) : (
                  row
                )}
              </li>
            )
          })}
        </ul>
      )}
    </ChartFrame>
  )
}

function EmptyPlot({ label }: { label?: string }) {
  return (
    <div className="flex min-h-12 flex-1 items-center gap-2 rounded-lg border border-dashed border-slate-200 px-3 py-2 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
      <Inbox className="h-4 w-4 shrink-0" aria-hidden />
      {label ?? '—'}
    </div>
  )
}

/** Category hues for the part-to-whole kinds; green and red stay out so a category never reads as a status. */
const CATEGORY_RAMP = [
  'var(--viz-s1)',
  'var(--viz-c5)',
  'var(--viz-c3)',
  'var(--viz-o1)',
  'var(--viz-c2)',
  'var(--viz-s2)',
]

function categoryColour(i: number, colors?: string[]): string {
  const ramp = colors ?? CATEGORY_RAMP
  return ramp[i % ramp.length]!
}

const MARK_KEYS = new Set(['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Escape'])

const plotFocusClass =
  'rounded-lg outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus'

/** One tab stop per chart: arrows walk the marks, Escape clears, and the pointer drives the same highlight. */
function useMarks(count: number) {
  const [active, setActive] = useState<number | null>(null)
  const plotProps = {
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (!MARK_KEYS.has(e.key)) return
      e.preventDefault()
      e.stopPropagation()
      setActive((cur) => stepActive(cur, e.key, count))
    },
    onBlur: () => setActive(null),
    onMouseLeave: () => setActive(null),
  }
  return { active, setActive, plotProps }
}

type PlotProps = ReturnType<typeof useMarks>['plotProps']

function ChartTip({
  x,
  y,
  title,
  value,
  detail,
  colour,
}: {
  x: number
  y: number
  title: string
  value: string
  detail?: string
  colour?: string
}) {
  const side = tipSide(x)
  const shift = side === 'start' ? '0%' : side === 'end' ? '-100%' : '-50%'
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute z-10 w-max max-w-56 select-none rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-lg shadow-slate-900/10 dark:border-slate-600 dark:bg-slate-900"
      style={{ left: `${x}%`, top: `${y}%`, transform: `translate(${shift}, calc(-100% - 8px))` }}
    >
      <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
        {colour && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colour }} />}
        {title}
      </div>
      <div className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{value}</div>
      {detail && <div className="text-slate-500 dark:text-slate-400">{detail}</div>}
    </div>
  )
}

function Readout({ text }: { text: string }) {
  return (
    <p className="sr-only" aria-live="polite">
      {text}
    </p>
  )
}

function describe(title: string, buckets: Bucket[], format: (n: number) => string): string {
  return `${title}: ${buckets.map((b) => `${b.label} ${format(b.value)}`).join(', ')}`
}

function LegendRows({
  buckets,
  format,
  shares,
  active,
  onHover,
  colors,
}: {
  buckets: Bucket[]
  format: (n: number) => string
  shares: number[]
  active: number | null
  onHover: (i: number | null) => void
  colors?: string[]
}) {
  return (
    <ul className="m-0 flex min-w-0 list-none flex-col gap-0.5 p-0" aria-hidden>
      {buckets.map((b, i) => (
        <li
          key={`${i}-${b.label}`}
          onMouseEnter={() => onHover(i)}
          className={`flex min-w-0 items-center gap-2 rounded-md px-1.5 py-0.5 text-xs motion-safe:transition-colors ${
            active === i ? 'bg-slate-100 dark:bg-slate-700/60' : ''
          }`}
        >
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: categoryColour(i, colors) }} />
          <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300" title={b.label}>
            {b.label}
          </span>
          <span className="shrink-0 tabular-nums font-medium text-slate-800 dark:text-slate-100">{format(b.value)}</span>
          <span className="w-9 shrink-0 text-right tabular-nums text-slate-500 dark:text-slate-400">{shares[i] ?? 0}%</span>
        </li>
      ))}
    </ul>
  )
}

function emptyFrame(title: string, subtitle?: string, hint?: string, emptyLabel?: string) {
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint} fill>
      <EmptyPlot label={emptyLabel} />
    </ChartFrame>
  )
}

const RING_R = 15.9155

const ARC_DRAW =
  'motion-safe:transition-[stroke-dasharray,stroke-width] motion-safe:duration-700 motion-safe:ease-out motion-safe:starting:[stroke-dasharray:0_100]'

/** Share of a whole with few parts: arcs sweep in from their start, the total sits in the hole and the legend carries every value. */
export function DonutChart({
  title,
  subtitle,
  hint,
  buckets,
  format,
  centerLabel,
  emptyLabel,
  colors,
  animate = false,
  compact = false,
}: {
  title: string
  subtitle?: string
  hint?: string
  buckets: Bucket[]
  format: (n: number) => string
  centerLabel: string
  emptyLabel?: string
  colors?: string[]
  animate?: boolean
  /** A smaller ring whose legend still fits beside it on a one-unit card. */
  compact?: boolean
}) {
  const { active, setActive, plotProps } = useMarks(buckets.length)
  const total = buckets.reduce((s, b) => s + Math.max(0, b.value), 0)
  if (total <= 0) return emptyFrame(title, subtitle, hint, emptyLabel)
  const arcs = donutArcs(buckets.map((b) => b.value))
  const shares = largestRemainder(
    buckets.map((b) => b.value),
    100,
    true,
  )
  const b = active === null ? null : buckets[active]
  const shownTotal = b ? format(b.value) : format(total)
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint}>
      <div className={compact ? '@container' : undefined}>
      <div
        className={
          compact
            ? 'flex flex-col items-center gap-3 @min-[19rem]:flex-row @min-[19rem]:gap-4'
            : 'flex flex-wrap items-center gap-4'
        }
      >
        <div
          {...plotProps}
          role="img"
          aria-label={describe(title, buckets, format)}
          className={`relative shrink-0 ${compact ? 'h-24 w-24' : 'h-36 w-36'} ${plotFocusClass}`}
        >
          <svg viewBox="0 0 42 42" className="h-full w-full -rotate-90 overflow-visible" aria-hidden>
            <circle cx="21" cy="21" r={RING_R} fill="none" stroke="var(--viz-grid)" strokeWidth="5" />
            {arcs.map((arc, k) => (
              <circle
                key={arc.index}
                cx="21"
                cy="21"
                r={RING_R}
                fill="none"
                pathLength={100}
                stroke={categoryColour(arc.index, colors)}
                strokeWidth={active === arc.index ? 7 : 5}
                strokeDasharray={`${arc.length} ${100 - arc.length}`}
                strokeDashoffset={-arc.start}
                opacity={active === null || active === arc.index ? 1 : 0.4}
                onMouseEnter={() => setActive(arc.index)}
                className={animate ? ARC_DRAW : 'motion-safe:transition-[stroke-width]'}
                style={animate ? { transitionDelay: barDelay(k) } : undefined}
              />
            ))}
          </svg>
          <div className={`pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center ${compact ? 'px-3' : 'px-5'}`}>
            <span className={`${compact ? 'text-base' : 'text-xl'} font-semibold tabular-nums leading-none text-slate-900 dark:text-slate-100`}>
              {shownTotal}
            </span>
            <span className="mt-1 line-clamp-2 text-[11px] leading-tight text-slate-500 dark:text-slate-400">
              {b ? b.label : centerLabel}
            </span>
          </div>
        </div>
        <div className={compact ? 'w-full min-w-0 @min-[19rem]:flex-1' : 'min-w-40 flex-1'}>
          <LegendRows buckets={buckets} format={format} shares={shares} active={active} onHover={setActive} colors={colors} />
        </div>
      </div>
      </div>
      <Readout text={b ? `${b.label} ${format(b.value)}, ${shares[active!] ?? 0}%` : ''} />
    </ChartFrame>
  )
}

/** A grid of squares, one per unit when the whole is small enough to count, so even a part of one stays visible. */
export function WaffleChart({
  title,
  subtitle,
  hint,
  buckets,
  format,
  unitLabel,
  emptyLabel,
  colors,
  animate = false,
}: {
  title: string
  subtitle?: string
  hint?: string
  buckets: Bucket[]
  format: (n: number) => string
  unitLabel: string
  emptyLabel?: string
  colors?: string[]
  animate?: boolean
}) {
  const { active, setActive, plotProps } = useMarks(buckets.length)
  const total = buckets.reduce((s, b) => s + Math.max(0, b.value), 0)
  if (total <= 0) return emptyFrame(title, subtitle, hint, emptyLabel)
  const unit = Number.isInteger(total) && total <= 200
  const cells = waffleCells(
    buckets.map((b) => b.value),
    unit ? total : 100,
  )
  const cols = waffleColumns(cells.length)
  const shares = largestRemainder(
    buckets.map((b) => b.value),
    100,
    true,
  )
  const b = active === null ? null : buckets[active]
  // The legend sits beside the squares and highlights the hovered category, so a floating tip would only repeat that row.
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint}>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="w-full max-w-[14rem]">
          <div
            {...plotProps}
            role="img"
            aria-label={describe(title, buckets, format)}
            className={`relative mb-1.5 ${plotFocusClass}`}
          >
            <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
              {cells.map((part, i) => (
                <span
                  key={i}
                  onMouseEnter={() => setActive(part)}
                  className={`aspect-square rounded-[3px] ${
                    animate
                      ? 'motion-safe:transition-[opacity,scale] motion-safe:duration-300 motion-safe:ease-out motion-safe:starting:scale-0 motion-safe:starting:opacity-0'
                      : 'motion-safe:transition-opacity'
                  }`}
                  style={{
                    background: categoryColour(part, colors),
                    opacity: active === null || active === part ? 1 : 0.25,
                    ...(animate ? { transitionDelay: `${Math.min(i * 5, 450)}ms` } : {}),
                  }}
                />
              ))}
            </div>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">{unitLabel}</p>
        </div>
        <div className="min-w-[9rem] flex-1">
          <LegendRows buckets={buckets} format={format} shares={shares} active={active} onHover={setActive} colors={colors} />
        </div>
      </div>
      <Readout text={b ? `${b.label} ${format(b.value)}, ${shares[active!] ?? 0}%` : ''} />
    </ChartFrame>
  )
}

function MarkRows({
  title,
  buckets,
  format,
  active,
  setActive,
  plotProps,
  position,
  stem,
  avatars,
}: {
  title: string
  buckets: Bucket[]
  format: (n: number) => string
  active: number | null
  setActive: (i: number | null) => void
  plotProps: PlotProps
  position: (v: number) => number
  stem: boolean
  avatars: boolean
}) {
  return (
    <ul
      {...plotProps}
      aria-label={describe(title, buckets, format)}
      className={`m-0 flex list-none flex-col gap-1 p-0 ${plotFocusClass}`}
    >
      {buckets.map((bk, i) => {
        const x = Math.min(100, Math.max(0, position(bk.value)))
        const on = active === i
        return (
          <li
            key={`${i}-${bk.label}`}
            onMouseEnter={() => setActive(i)}
            className={`flex items-center gap-2 rounded-md px-1.5 py-0.5 text-xs motion-safe:transition-colors ${
              on ? 'bg-slate-100 dark:bg-slate-700/60' : ''
            }`}
          >
            {avatars && (
              <span
                aria-hidden
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200"
              >
                {initials(bk.label)}
              </span>
            )}
            <span className="w-[38%] max-w-52 shrink-0 line-clamp-2 leading-tight text-slate-600 dark:text-slate-300" title={bk.label}>
              {bk.label}
            </span>
            <span className="relative h-5 min-w-0 flex-1">
              {stem ? (
                <span
                  aria-hidden
                  className={`absolute left-0 top-1/2 h-3.5 min-w-1 -translate-y-1/2 rounded-sm motion-safe:transition-opacity ${BAR_GROW_X} ${
                    active === null || on ? '' : 'opacity-60'
                  }`}
                  style={{ width: `${x}%`, background: 'var(--viz-s1)', transitionDelay: barDelay(i) }}
                />
              ) : (
                <>
                  {/* Dotted, not a solid track: a solid line under a round dot reads as a slider. */}
                  <span aria-hidden className="absolute inset-x-0 top-1/2 border-t border-dotted border-slate-300 dark:border-slate-600" />
                  <span
                    aria-hidden
                    className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full motion-safe:transition-[width,height,left] motion-safe:duration-500 ${
                      on ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5'
                    }`}
                    style={{ left: `${x}%`, background: 'var(--viz-s1)' }}
                  />
                </>
              )}
            </span>
            <span className="w-12 shrink-0 text-right tabular-nums font-medium text-slate-800 dark:text-slate-100">
              {format(bk.value)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/** A ranked comparison as thick bars from zero, with optional initials beside each row; no knob, so it never reads as a slider. */
export function LollipopChart({
  title,
  subtitle,
  hint,
  buckets,
  format,
  emptyLabel,
  avatars = false,
}: {
  title: string
  subtitle?: string
  hint?: string
  buckets: Bucket[]
  format: (n: number) => string
  emptyLabel: string
  avatars?: boolean
}) {
  const { active, setActive, plotProps } = useMarks(buckets.length)
  if (buckets.length === 0) return emptyFrame(title, subtitle, hint, emptyLabel)
  const max = Math.max(1, ...buckets.map((b) => b.value))
  const b = active === null ? null : buckets[active]
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint}>
      <MarkRows
        title={title}
        buckets={buckets}
        format={format}
        active={active}
        setActive={setActive}
        plotProps={plotProps}
        position={(v) => (Math.max(0, v) / max) * 100}
        stem
        avatars={avatars}
      />
      <Readout text={b ? `${b.label} ${format(b.value)}` : ''} />
    </ChartFrame>
  )
}

/** Values packed close together: dots on an axis that starts near the data, with the axis ends printed so the crop is never hidden. */
export function DotPlot({
  title,
  subtitle,
  hint,
  buckets,
  format,
  emptyLabel,
}: {
  title: string
  subtitle?: string
  hint?: string
  buckets: Bucket[]
  format: (n: number) => string
  emptyLabel: string
}) {
  const { active, setActive, plotProps } = useMarks(buckets.length)
  if (buckets.length === 0) return emptyFrame(title, subtitle, hint, emptyLabel)
  const { lo, hi } = dotDomain(buckets.map((b) => b.value))
  const b = active === null ? null : buckets[active]
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint}>
      <div aria-hidden className="mb-1 flex items-center gap-2 px-1.5 text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
        <span className="w-[34%] max-w-32 shrink-0" />
        <span className="flex min-w-0 flex-1 justify-between">
          <span>{format(lo)}</span>
          <span>{format(Math.round((lo + hi) / 2))}</span>
          <span>{format(hi)}</span>
        </span>
        <span className="w-12 shrink-0" />
      </div>
      <MarkRows
        title={title}
        buckets={buckets}
        format={format}
        active={active}
        setActive={setActive}
        plotProps={plotProps}
        position={(v) => ((v - lo) / (hi - lo)) * 100}
        stem={false}
        avatars={false}
      />
      <Readout text={b ? `${b.label} ${format(b.value)}` : ''} />
    </ChartFrame>
  )
}

/** Part-to-whole across many parts: one hue, area for size and shade for rank; parts too small to label fold into one "Other" tile, named under the map. */
export function TreemapChart({
  title,
  subtitle,
  hint,
  buckets,
  format,
  otherLabel,
  emptyLabel,
  animate = false,
}: {
  title: string
  subtitle?: string
  hint?: string
  buckets: Bucket[]
  format: (n: number) => string
  otherLabel: string
  emptyLabel?: string
  animate?: boolean
}) {
  const [plot, setPlot] = useState<HTMLDivElement | null>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    if (!plot) return
    const measure = () => setSize({ w: plot.clientWidth, h: plot.clientHeight })
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(plot)
    return () => ro.disconnect()
  }, [plot])
  const { tiles, folded } = treemapTiles(buckets, size.w, size.h, format, otherLabel)
  const { active, setActive, plotProps } = useMarks(tiles.length)
  if (!buckets.some((b) => b.value > 0)) return emptyFrame(title, subtitle, hint, emptyLabel)
  const byValue = tiles.map((_, k) => k).sort((a, b) => tiles[b]!.value - tiles[a]!.value)
  const rank = new Map(byValue.map((k, r) => [k, r]))
  const strength = (k: number) => tileShade(rank.get(k) ?? 0, tiles.length)
  const otherAt = tiles.findIndex((t) => t.index === null)
  const tile = active === null ? undefined : tiles[active]
  const pct = (n: number, of: number) => (of > 0 ? (n / of) * 100 : 0)
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint}>
      <div
        {...plotProps}
        ref={setPlot}
        role="img"
        aria-label={describe(title, buckets, format)}
        className={`relative h-48 ${plotFocusClass}`}
      >
        {tiles.map((r, k) => {
          return (
            <div
              key={r.index ?? 'other'}
              onMouseEnter={() => setActive(k)}
              className={`absolute overflow-hidden rounded-md p-1.5 ${
                animate
                  ? 'motion-safe:transition-[opacity,scale] motion-safe:duration-500 motion-safe:ease-out motion-safe:starting:scale-90 motion-safe:starting:opacity-0'
                  : ''
              } ${active !== null && active !== k ? 'opacity-50' : ''}`}
              style={{
                left: r.x + 1.5,
                top: r.y + 1.5,
                width: Math.max(0, r.w - 3),
                height: Math.max(0, r.h - 3),
                background: `color-mix(in srgb, var(--viz-s1) ${strength(k)}%, transparent)`,
                ...(animate ? { transitionDelay: barDelay(k) } : {}),
              }}
            >
              <span
                aria-hidden
                className="block text-xs leading-tight text-slate-900 dark:text-white"
              >
                <span className="block break-words font-medium">{r.label}</span>
                <span className="block tabular-nums">{format(r.value)}</span>
              </span>
            </div>
          )
        })}
        {tile && (
          <ChartTip
            x={pct(tile.x + tile.w / 2, size.w)}
            y={pct(tile.y, size.h) + 4}
            title={tile.label}
            value={format(tile.value)}
          />
        )}
      </div>
      {folded.length > 0 && otherAt >= 0 && (
        <ul aria-hidden className="m-0 mt-2 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-xs">
          {folded.map((i) => (
            <li key={i} className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ background: `color-mix(in srgb, var(--viz-s1) ${strength(otherAt)}%, transparent)` }}
              />
              {buckets[i]!.label}
              <span className="tabular-nums font-medium text-slate-800 dark:text-slate-100">
                {format(buckets[i]!.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Readout text={tile ? `${tile.label} ${format(tile.value)}` : ''} />
    </ChartFrame>
  )
}

export interface SeriesPoint {
  label: string
  value: number
  detail?: string
}

function svgId(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_-]/g, '')
}

/** A run of days or weekdays: a smooth line over a fading fill from a zero baseline, swept in left to right. */
export function AreaChart({
  title,
  subtitle,
  hint,
  points,
  format,
  emptyLabel,
  caption,
  animate = false,
  compact = false,
}: {
  title: string
  subtitle?: string
  hint?: string
  points: SeriesPoint[]
  format: (n: number) => string
  emptyLabel?: string
  caption?: string
  animate?: boolean
  /** A shorter plot for a card that shares a slide with small neighbours. */
  compact?: boolean
}) {
  const id = svgId(useId())
  const { active, setActive, plotProps } = useMarks(points.length)
  if (points.length === 0 || points.every((p) => p.value === 0)) return emptyFrame(title, subtitle, hint, emptyLabel)
  const max = Math.max(0, ...points.map((p) => p.value))
  const xy = seriesPoints(
    points.map((p) => p.value),
    100,
    100,
  ).map((pt) => ({ x: pt.x, y: 8 + pt.y * 0.92 }))
  const step = axisLabelStep(points.length, 5)
  const p = active === null ? null : points[active]
  const at = active === null ? null : xy[active]
  const dots = points.length <= 16
  return (
    <ChartFrame title={title} subtitle={subtitle} hint={hint}>
      <div className="flex gap-2">
        <div aria-hidden className={`flex flex-col justify-between ${compact ? 'h-28' : 'h-40'} pt-1 text-[11px] tabular-nums text-slate-500 dark:text-slate-400`}>
          <span>{format(max)}</span>
          <span>{format(0)}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div
            {...plotProps}
            role="img"
            aria-label={`${title}: ${points.map((pt) => `${pt.label} ${format(pt.value)}`).join(', ')}`}
            className={`relative mx-1.5 ${compact ? 'h-28' : 'h-40'} ${plotFocusClass}`}
          >
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
              <defs>
                <linearGradient id={`${id}-fill`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="var(--viz-s1)" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="var(--viz-s1)" stopOpacity="0" />
                </linearGradient>
                <clipPath id={`${id}-clip`}>
                  <rect
                    x="-2"
                    y="-10"
                    width="104"
                    height="120"
                    className={
                      animate
                        ? 'origin-left motion-safe:transition-transform motion-safe:duration-1000 motion-safe:ease-out motion-safe:starting:scale-x-0'
                        : ''
                    }
                  />
                </clipPath>
              </defs>
              {[8, 54].map((y) => (
                <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="var(--viz-grid)" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
              ))}
              <line x1="0" x2="100" y1="100" y2="100" stroke="var(--viz-axis)" vectorEffect="non-scaling-stroke" />
              <g clipPath={`url(#${id}-clip)`}>
                <path d={smoothAreaPath(xy, 100)} fill={`url(#${id}-fill)`} />
                <path
                  d={smoothPath(xy)}
                  fill="none"
                  stroke="var(--viz-s1)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            </svg>
            {at && <span aria-hidden className="absolute inset-y-0 w-px bg-slate-300 dark:bg-slate-600" style={{ left: `${at.x}%` }} />}
            {xy.map((pt, i) =>
              dots || i === active ? (
                <span
                  key={i}
                  aria-hidden
                  className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-white ring-2 motion-safe:transition-[width,height] dark:bg-slate-800 ${
                    i === active ? 'h-3 w-3' : 'h-2 w-2'
                  } ${animate ? 'motion-safe:transition-opacity motion-safe:duration-300 motion-safe:starting:opacity-0' : ''}`}
                  style={{
                    left: `${pt.x}%`,
                    top: `${pt.y}%`,
                    ['--tw-ring-color' as string]: 'var(--viz-s1)',
                    ...(animate ? { transitionDelay: `${300 + (i / Math.max(1, xy.length - 1)) * 700}ms` } : {}),
                  }}
                />
              ) : null,
            )}
            <div className="absolute inset-0 flex">
              {points.map((pt, i) => (
                <span key={`${i}-${pt.label}`} className="h-full flex-1" onMouseEnter={() => setActive(i)} />
              ))}
            </div>
            {p && at && <ChartTip x={at.x} y={at.y} title={p.label} value={format(p.value)} detail={p.detail} />}
          </div>
          <div aria-hidden className="mx-1.5 mt-1 flex justify-between text-[11px] text-slate-500 dark:text-slate-400">
            {points.map((pt, i) =>
              i % step === 0 || i === points.length - 1 ? (
                <span key={`${i}-${pt.label}`} className="whitespace-nowrap">
                  {pt.label}
                </span>
              ) : null,
            )}
          </div>
        </div>
      </div>
      {caption && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{caption}</p>}
      <Readout text={p ? `${p.label} ${format(p.value)}${p.detail ? `, ${p.detail}` : ''}` : ''} />
    </ChartFrame>
  )
}

export interface SparkPoint {
  label: string
  value: string
}

/** A trend small enough to sit inside a tile, ending on a dot at the latest value; with `points` (one per value) a hover, drag, tap or arrow key moves the dot and shows that point. */
export function Sparkline({ values, label, points }: { values: number[]; label: string; points?: SparkPoint[] }) {
  const id = svgId(useId())
  const [active, setActive] = useState<number | null>(null)
  const [pressed, setPressed] = useState(false)
  if (values.length < 2) return null
  const xy = seriesPoints(values, 100, 100).map((pt) => ({ x: pt.x, y: 10 + pt.y * 0.85 }))
  const scrub = points !== undefined && points.length === values.length
  const shown = scrub && active !== null ? active : null
  const dot = xy[shown ?? xy.length - 1]!
  const readout = shown !== null ? points![shown]! : null
  const pick = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    if (r.width > 0) setActive(nearestPointIndex((e.clientX - r.left) / r.width, values.length))
  }
  const align = readoutAlign(dot.x)
  const interactive = scrub
    ? {
        role: 'slider',
        tabIndex: 0,
        'aria-valuemin': 0,
        'aria-valuemax': values.length - 1,
        'aria-valuenow': shown ?? values.length - 1,
        'aria-valuetext': `${points![shown ?? values.length - 1]!.label}: ${points![shown ?? values.length - 1]!.value}`,
        onPointerDown: (e: ReactPointerEvent<HTMLSpanElement>) => {
          try {
            e.currentTarget.setPointerCapture(e.pointerId)
          } catch {
            // A pointer the browser no longer tracks cannot be captured; the drag still follows it inside the line.
          }
          setPressed(true)
          pick(e)
        },
        onPointerMove: (e: ReactPointerEvent<HTMLSpanElement>) => {
          if (pressed || e.pointerType !== 'touch') pick(e)
        },
        onPointerUp: () => setPressed(false),
        onPointerCancel: () => setPressed(false),
        onPointerLeave: (e: ReactPointerEvent<HTMLSpanElement>) => {
          if (e.pointerType !== 'touch' && !pressed) setActive(null)
        },
        onKeyDown: (e: ReactKeyboardEvent<HTMLSpanElement>) => {
          const next = steppedPointIndex(e.key, active, values.length)
          if (next === null) return
          e.preventDefault()
          setActive(next)
        },
        onFocus: () => setActive((current) => current ?? values.length - 1),
        onBlur: () => setActive(null),
      }
    : { role: 'img' }
  return (
    <span
      {...interactive}
      aria-label={label}
      className={`viz-root relative block h-9 w-full max-w-32${
        scrub ? ' cursor-crosshair touch-pan-y select-none rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus' : ''
      }`}
    >
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
        <defs>
          <linearGradient id={`${id}-fill`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--viz-s1)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--viz-s1)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={smoothAreaPath(xy, 100)} fill={`url(#${id}-fill)`} />
        <path d={smoothPath(xy)} fill="none" stroke="var(--viz-s1)" strokeWidth="1.75" vectorEffect="non-scaling-stroke" />
      </svg>
      {readout && (
        <span aria-hidden className="pointer-events-none absolute inset-y-0 w-px bg-slate-400/70 dark:bg-slate-500/70" style={{ left: `${dot.x}%` }} />
      )}
      <span
        aria-hidden
        className={`pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white dark:ring-slate-800 ${readout ? 'h-2.5 w-2.5' : 'h-2 w-2'}`}
        style={{ left: `${dot.x}%`, top: `${dot.y}%`, background: 'var(--viz-s1)' }}
      />
      {readout && (
        <span
          aria-hidden
          className={`pointer-events-none absolute bottom-full z-10 mb-1 whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 text-[11px] leading-tight text-white shadow-sm dark:bg-slate-100 dark:text-slate-900 ${
            align === 'center' ? '-translate-x-1/2' : align === 'end' ? '-translate-x-full' : ''
          }`}
          style={{ left: `${dot.x}%` }}
        >
          <span className="font-semibold tabular-nums">{readout.value}</span> {readout.label}
        </span>
      )}
    </span>
  )
}

/** How full something is, as a track inside a tile; an unknown total draws an empty track, never a full one. */
export function MiniMeter({ value, total, label }: { value: number; total: number; label: string }) {
  return (
    <span role="img" aria-label={label} className="viz-root block h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
      <span
        className={`block h-full rounded-full ${BAR_GROW_X}`}
        style={{ width: `${fraction(value, total) * 100}%`, background: 'var(--viz-s1)' }}
      />
    </span>
  )
}

/** A share bar small enough for a tile; the tile's own text names the parts. */
export function MiniShareBar({ buckets, label, colors }: { buckets: Bucket[]; label: string; colors?: string[] }) {
  const total = buckets.reduce((s, b) => s + Math.max(0, b.value), 0)
  if (total <= 0) return null
  return (
    <span role="img" aria-label={label} className={`viz-root flex h-2 w-full gap-px overflow-hidden rounded-full ${BAR_GROW_X}`}>
      {buckets.map((b, i) =>
        b.value > 0 ? (
          <span
            key={`${i}-${b.label}`}
            className="h-full"
            style={{ flexGrow: b.value, flexBasis: 0, minWidth: 3, background: categoryColour(i, colors) }}
          />
        ) : null,
      )}
    </span>
  )
}
